import React, { useState } from "react";
import { useWallet } from "@/context/WalletContext";
import { shortAddr } from "@/lib/format";
import { Wallet, LogOut, Copy, ChevronDown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const WALLETS = [
  { name: "MetaMask", icon: "🦊", tag: "Popular" },
  { name: "Trust Wallet", icon: "🛡️", tag: "" },
  { name: "TokenPocket", icon: "🪙", tag: "" },
  { name: "WalletConnect", icon: "🔗", tag: "QR" },
];

export default function WalletButton() {
  const { address, balance, connect, disconnect, connecting, networkName, isBSC, connectDemo, connectWalletConnect } = useWallet();
  const [open, setOpen] = useState(false);

  const pick = (name) => {
    setOpen(false);
    if (name === "MetaMask") connect(name);
    else connectWalletConnect();
  };

  if (address) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="wallet-account-button"
            className="flex items-center gap-2 rounded-xl border border-[#1CFD4C]/30 bg-[#0F1712] px-3 py-2 hover:border-[#1CFD4C]/60 transition-colors"
          >
            <span className={`w-2 h-2 rounded-full live-dot ${isBSC ? "bg-emerald-400" : "bg-amber-400"}`} />
            <div className="text-left leading-tight">
              <div className="font-num text-sm text-white">{shortAddr(address)}</div>
              <div className="text-[10px] text-muted-foreground">{networkName}</div>
            </div>
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#0F1712] border-[#1CFD4C]/30 backdrop-blur-xl w-56">
          <div className="px-3 py-2 border-b border-white/10">
            <div className="text-[10px] uppercase text-muted-foreground tracking-widest">BNB Balance</div>
            <div className="font-num text-lg gold-text">{balance ?? "0.0000"} BNB</div>
          </div>
          <DropdownMenuItem
            data-testid="wallet-copy-address"
            onClick={() => { navigator.clipboard.writeText(address); toast.success("Address copied"); }}
            className="cursor-pointer gap-2"
          >
            <Copy className="w-4 h-4" /> Copy Address
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="wallet-disconnect-button"
            onClick={disconnect}
            className="cursor-pointer gap-2 text-red-400 focus:text-red-400"
          >
            <LogOut className="w-4 h-4" /> Disconnect
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      <button
        data-testid="wallet-connect-button"
        onClick={() => setOpen(true)}
        disabled={connecting}
        className="flex items-center gap-2 rounded-xl bg-[#1CFD4C] px-4 py-2.5 font-semibold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60 glow-gold"
      >
        <Wallet className="w-4 h-4" />
        {connecting ? "Connecting…" : "Connect Wallet"}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F1712] border border-[#1CFD4C]/30 backdrop-blur-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Connect Wallet</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Choose a wallet to enter the CROC PROTOCOL dApp.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 mt-2">
            {WALLETS.map((w) => (
              <button
                key={w.name}
                data-testid={`wallet-option-${w.name.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => pick(w.name)}
                className="flex items-center justify-between rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3.5 hover:border-[#1CFD4C]/50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{w.icon}</span>
                  <span className="font-medium text-white">{w.name}</span>
                </div>
                {w.tag && <span className="text-[10px] uppercase tracking-widest gold-text">{w.tag}</span>}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
            <ShieldCheck className="w-4 h-4 emerald-text" /> Non-custodial · we never hold your keys
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
